import { Component, Input, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CareerGoalService } from '@app/services/api/career-goal/career-goal.service';
import { ChecklistService } from '@app/services/api/checklist/checklist.service';
import { setCareerGoalByOnetCode } from '@app/state/actions/careers.action';
import { selectCareerGoal } from '@app/state/selectors/career.selector';
import { Store } from '@ngrx/store';
import { finalize, take } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerModule } from "ngx-spinner";
import { 
  BYP_PLAN_GENERATED_LOCAL_KEY, 
  CAREER_GOAL_LOCAL_KEY, 
  ONET_LOCAL_KEY 
} from '@app/utils/constants';
import { analytics } from '@app/events/segment/analytics';
import { updateJobUserContext, updateUserJobFilterOptions } from '@app/state/actions/jobs.action';
import { JobFilterFormDataSchema } from '@app/schemas/job-filter';

@Component({
  selector: 'app-career-goal',
  imports: [
    RouterLink,
    NgxSpinnerModule,
  ],
  templateUrl: './career-goal.component.html',
  styleUrl: './career-goal.component.css'
})
export class CareerGoalComponent implements OnInit {
  @Input() onetCode!: string;
  @Input() title: string = "";
  public userCareerGoal: string = '';
  public userFilterOptions!: JobFilterFormDataSchema;
  public isLoading: boolean = true;
  public loadingMessage: string = '';

  constructor(
    private store: Store<{ 
      adeptIDCareers: any,
      jobFilter: JobFilterFormDataSchema, 
    }>,
    private goalApiService: CareerGoalService,
    private checklistService: ChecklistService,
    private spinner: NgxSpinnerService,
  ) {
    this.store.select('jobFilter').subscribe(value => this.userFilterOptions = value);
  }

  ngOnInit() {
    this.store.select(selectCareerGoal).subscribe(careerGoal => {
      this.userCareerGoal = careerGoal;
      if(!this.userCareerGoal) {
        this.getUserCareerGoal();
      } else {
        this.isLoading = false;
      }
    });
  }

  getUserCareerGoal() {
    this.checklistService.getChecklistStatus().subscribe({
      next: (response) => {
        if (response.career_goal) {
          this.userCareerGoal = response.career_goal;
          this.store.dispatch(setCareerGoalByOnetCode({ onetCode: response.career_goal }));
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching checklist status:', err);
        this.isLoading = false;
      },
    });
  }

  setCareerGoal() {
    // Track intent to set career goal
    analytics.track('Goal Set', { section: 'explore', career: this.onetCode });
    this.callCareerGoalApi();
  }

  callCareerGoalApi() {
    this.spinner.show();
    this.loadingMessage = 'Setting career goal…';
    this.goalApiService.setCareerGoalApi({ 
      onet_code: this.onetCode 
    }).pipe(
      finalize(() => {
        this.spinner.hide();
        this.loadingMessage = 'Career goal updated.';
      })
    ).subscribe({
      next: () => this.updateCareerGoal(this.onetCode),
      error: (err) => console.log(err),
    });
  }

  replaceCareerGoal() {
    // Track replace of career goal
    analytics.track('Goal Set', { section: 'explore', career: this.onetCode });
    this.callCareerGoalApi();
  }

  unsetCareerGoal() {
    // Track unset of career goal
    analytics.track('Goal Unset', { section: 'explore', career: this.onetCode });
    this.spinner.show();
    this.loadingMessage = 'Removing career goal…';
    this.goalApiService.unsetCareerGoalApi().pipe(
      finalize(() => {
        this.spinner.hide();
        this.loadingMessage = 'Career goal removed.';
      })
    ).subscribe({
      next: () => this.updateCareerGoal(""),
      error: (err) => console.log(err),
    });
  }

  updateCareerGoal(code: string) {
    this.store.dispatch(setCareerGoalByOnetCode({ onetCode: code }));
    // to reflect the career goal change in job recommendations, we need to update the job user context in the store as well
    this.store.dispatch(updateJobUserContext({ userContext: 
      { careerGoal: {
        onetCode: code, careerName: this.title, careerCluster: []
      }} }));
    
    /**
     * Once career goal is set/updated/unset,
     * update related_to_career_goal as true/false accordingly in the job filter options
     */
    this.store.dispatch(updateUserJobFilterOptions({ filter: {
      ...this.userFilterOptions,
      is_related_to_career_goal: code ? true : false,
    }}));
    
    localStorage.setItem(CAREER_GOAL_LOCAL_KEY, code);
    localStorage.setItem(ONET_LOCAL_KEY, code);
    localStorage.setItem(BYP_PLAN_GENERATED_LOCAL_KEY, "false");
  }
}
